import { useState, useEffect, useRef } from 'react'
import { useFacilityStore } from '../store/facilityStore'
import { apiFetch } from '../lib/apiFetch'
import { CloseIcon, PlusIcon } from './icons/Icons'
import { TEST_USERS, getUserByName } from '../data/testUsers'

const STATUSES = [
  { key: 'todo',        label: 'TO DO',       color: '#6b7280' },
  { key: 'in_progress', label: 'IN PROGRESS', color: '#f59e0b' },
  { key: 'done',        label: 'DONE',        color: '#4ade80' },
]

const NEXT_STATUS   = { todo: 'in_progress', in_progress: 'done', done: 'todo' }
const PRIORITY_COLOR = { low: '#6b7280', normal: '#60a5fa', high: '#f87171' }

// ── Avatar bubble ────────────────────────────────────────────────────────────
function Avatar({ user, size = 28, active, onClick, title, style }) {
  const [imgErr, setImgErr] = useState(false)
  return (
    <button
      className={`wb-avatar${active ? ' wb-avatar--active' : ''}`}
      style={{ '--av-color': user.color, width: size, height: size, fontSize: size * 0.36, ...style }}
      onClick={onClick}
      title={title ?? user.name}
      type="button"
    >
      {!imgErr
        ? <img src={user.avatarUrl} alt={user.initials} onError={() => setImgErr(true)} draggable={false} />
        : <span>{user.initials}</span>
      }
    </button>
  )
}

// ── Assign picker ────────────────────────────────────────────────────────────
function AssignPicker({ current, onAssign, onClear, onClose }) {
  const ref = useRef(null)
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  return (
    <div className="wb-assign-picker" ref={ref}>
      <div className="wb-assign-picker-header">
        <span>Assign to</span>
        {current && (
          <button className="wb-assign-clear" onClick={onClear} type="button">✕ unassign</button>
        )}
      </div>
      <div className="wb-assign-picker-list">
        {TEST_USERS.map(u => (
          <button
            key={u.id}
            className={`wb-assign-user${current === u.name ? ' active' : ''}`}
            onClick={() => onAssign(u)}
            type="button"
          >
            <Avatar user={u} size={22} />
            <span>{u.name}</span>
            {current === u.name && <span className="wb-assign-check">✓</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────
export default function WhiteboardPanel({ open, onClose }) {
  const rooms      = useFacilityStore(s => s.rooms)
  const tasks      = useFacilityStore(s => s.tasks)
  const loadTasks  = useFacilityStore(s => s.loadTasks)
  const addTask    = useFacilityStore(s => s.addTask)
  const updateTask = useFacilityStore(s => s.updateTask)
  const removeTask = useFacilityStore(s => s.deleteTask)

  const [loading, setLoading]     = useState(false)
  const [addOpen, setAddOpen]     = useState(false)
  const [form, setForm]           = useState({ title: '', description: '', roomId: '', assignedTo: '', priority: 'normal', dueDate: '' })
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState(null)
  // active user filter (null = show all)
  const [filterUser, setFilterUser] = useState(null)
  // which task's assign picker is open
  const [pickerTaskId, setPickerTaskId] = useState(null)

  useEffect(() => {
    if (!open) return
    if (tasks.length === 0) {
      setLoading(true)
      loadTasks().finally(() => setLoading(false))
    }
  }, [open])

  // Close picker when board closes
  useEffect(() => { if (!open) setPickerTaskId(null) }, [open])

  const cycleStatus = async (task) => {
    const next = NEXT_STATUS[task.status]
    updateTask(task.id, { status: next })
    try {
      await apiFetch(`/api/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify({ status: next }) })
    } catch {
      updateTask(task.id, { status: task.status })
    }
  }

  const assignTask = async (task, user) => {
    setPickerTaskId(null)
    const name = user?.name ?? null
    updateTask(task.id, { assignedTo: name })
    try {
      await apiFetch(`/api/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify({ assignedTo: name }) })
    } catch {
      updateTask(task.id, { assignedTo: task.assignedTo })
    }
  }

  const deleteTask = async (id) => {
    removeTask(id)
    try {
      await apiFetch(`/api/tasks/${id}`, { method: 'DELETE' })
    } catch { loadTasks() }
  }

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return
    setSaving(true); setError(null)
    try {
      const task = await apiFetch('/api/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title:       form.title.trim(),
          description: form.description.trim() || undefined,
          roomId:      form.roomId || undefined,
          assignedTo:  form.assignedTo || undefined,
          priority:    form.priority,
          dueDate:     form.dueDate || undefined,
        }),
      })
      addTask(task)
      setForm({ title: '', description: '', roomId: '', assignedTo: '', priority: 'normal', dueDate: '' })
      setAddOpen(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const interactiveRooms = rooms.filter(r => r.interactive !== false && r.id && !r.id.startsWith('_'))

  const filteredTasks = filterUser
    ? tasks.filter(t => t.assignedTo === filterUser.name)
    : tasks

  const byStatus = (status) => filteredTasks.filter(t => t.status === status)

  return (
    <>
      {/* Backdrop */}
      <div className={`wb-backdrop ${open ? 'open' : ''}`} onClick={onClose} aria-hidden />

      <aside className={`wb-panel ${open ? 'open' : ''}`} aria-label="Task whiteboard">

        {/* Header */}
        <div className="wb-header">
          <span className="wb-title">WHITEBOARD</span>
          <button
            className="wb-add-btn"
            onClick={() => setAddOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {addOpen ? <><CloseIcon width="14" height="14" style={{marginRight: 4}} /> Cancel</> : <><PlusIcon width="14" height="14" style={{marginRight: 4}} /> Task</>}
          </button>
          <button className="wb-close" onClick={onClose}><CloseIcon width="16" height="16" /></button>
        </div>

        {/* User bubble row */}
        <div className="wb-users-row">
          {/* "All" button */}
          <button
            className={`wb-all-btn${!filterUser ? ' active' : ''}`}
            onClick={() => setFilterUser(null)}
            title="Show all tasks"
            type="button"
          >
            ALL
          </button>
          {TEST_USERS.map(u => (
            <Avatar
              key={u.id}
              user={u}
              size={30}
              active={filterUser?.id === u.id}
              onClick={() => setFilterUser(prev => prev?.id === u.id ? null : u)}
            />
          ))}
          {filterUser && (
            <span className="wb-filter-label">
              {filterUser.name.split(' ')[0]}'s tasks
            </span>
          )}
        </div>

        {/* Add task form */}
        {addOpen && (
          <form className="wb-add-form" onSubmit={handleAdd}>
            <input
              className="wb-input"
              placeholder="Task title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              autoFocus
            />
            <textarea
              className="wb-input wb-textarea"
              placeholder="Description (optional)"
              rows={2}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
            <div className="wb-form-row">
              <select className="wb-input wb-select" value={form.roomId} onChange={e => setForm(f => ({ ...f, roomId: e.target.value }))}>
                <option value="">No room</option>
                {interactiveRooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <select className="wb-input wb-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
            {/* Assignee selector in form */}
            <div className="wb-form-assign-row">
              <span className="wb-form-assign-label">Assign:</span>
              <button
                type="button"
                className={`wb-form-unassign${!form.assignedTo ? ' active' : ''}`}
                onClick={() => setForm(f => ({ ...f, assignedTo: '' }))}
              >—</button>
              {TEST_USERS.map(u => (
                <Avatar
                  key={u.id}
                  user={u}
                  size={26}
                  active={form.assignedTo === u.name}
                  onClick={() => setForm(f => ({ ...f, assignedTo: f.assignedTo === u.name ? '' : u.name }))}
                  title={u.name}
                />
              ))}
            </div>
            <input
              className="wb-input"
              type="date"
              title="Due date (optional)"
              value={form.dueDate}
              onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
            />
            {error && <p className="form-error">{error}</p>}
            <button className="btn-primary" type="submit" disabled={saving || !form.title.trim()}>
              {saving ? 'Adding…' : 'Add Task'}
            </button>
          </form>
        )}

        {/* Columns */}
        <div className="wb-board">
          {loading && <p className="wb-loading">Loading…</p>}
          {STATUSES.map(col => {
            const colTasks = byStatus(col.key)
            return (
              <div key={col.key} className="wb-col">
                <div className="wb-col-header">
                  <span className="wb-col-dot" style={{ background: col.color }} />
                  <span className="wb-col-label">{col.label}</span>
                  <span className="wb-col-count">{colTasks.length}</span>
                </div>
                <div className="wb-col-body">
                  {colTasks.length === 0 && <p className="wb-empty">—</p>}
                  {colTasks.map(task => {
                    const room       = task.roomId ? rooms.find(r => r.id === task.roomId) : null
                    const assignee   = getUserByName(task.assignedTo)
                    const pickerOpen = pickerTaskId === task.id
                    return (
                      <div key={task.id} className="wb-card">
                        <div className="wb-card-top">
                          <span
                            className="wb-priority-dot"
                            style={{ background: PRIORITY_COLOR[task.priority] ?? PRIORITY_COLOR.normal }}
                            title={task.priority}
                          />
                          <span className="wb-card-title">{task.title}</span>
                          <button className="wb-card-delete" onClick={() => deleteTask(task.id)} aria-label="Delete task">
                            <CloseIcon width="14" height="14" />
                          </button>
                        </div>
                        {task.description && <p className="wb-card-desc">{task.description}</p>}
                        <div className="wb-card-footer">
                          {room && <span className="wb-card-room">{room.name}</span>}
                          {task.dueDate && (
                            <span className="wb-task-due">
                              {new Date(task.dueDate.includes('T') ? task.dueDate : task.dueDate + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {/* Assignee chip or assign button */}
                          <div className="wb-assign-wrap" style={{ position: 'relative' }}>
                            {assignee ? (
                              <Avatar
                                user={assignee}
                                size={22}
                                onClick={() => setPickerTaskId(pickerOpen ? null : task.id)}
                                title={`Assigned: ${assignee.name} — click to change`}
                              />
                            ) : (
                              <button
                                className="wb-assign-empty-btn"
                                onClick={() => setPickerTaskId(pickerOpen ? null : task.id)}
                                title="Assign to someone"
                                type="button"
                              >+</button>
                            )}
                            {pickerOpen && (
                              <AssignPicker
                                current={task.assignedTo}
                                onAssign={(u) => assignTask(task, u)}
                                onClear={() => assignTask(task, null)}
                                onClose={() => setPickerTaskId(null)}
                              />
                            )}
                          </div>
                          <button
                            className="wb-status-btn"
                            style={{ borderColor: col.color, color: col.color }}
                            onClick={() => cycleStatus(task)}
                            title="Cycle status"
                          >
                            {col.label}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </aside>
    </>
  )
}
